import Phaser from 'phaser'
import { TOWERS, TOWER_ORDER, type TowerDef } from './towers'
import { getWave } from './enemies'
import { PATH_POINTS, PATH_WIDTH } from './path'
import { MOBILE_PATH_POINTS, MOBILE_PATH_WIDTH } from './path-mobile'
import { GAME_WIDTH, GAME_HEIGHT } from './config'
import { TOWER_SPRITES, ENEMY_SPRITES, ENVIRONMENT_SPRITES, ANIMATION_SPRITES, EXPLOSION_SPRITES, PROJECTILE_SPRITES, TUB_SCALES, TIERED_SPRITES, getTierIndex, type TowerType } from './sprites'

interface Enemy {
  graphics: Phaser.GameObjects.Container
  hp: number
  maxHp: number
  speed: number
  baseSpeed: number
  pathIndex: number
  pathProgress: number
  alive: boolean
  type: string
  slowTimer: number
  hpBar: Phaser.GameObjects.Graphics
}

interface TowerUpgrades {
  damage: number   // 0-3
  range: number    // 0-3
  speed: number    // 0-3
}

interface Tower {
  container: Phaser.GameObjects.Container
  def: TowerDef
  x: number
  y: number
  fireTimer: number
  rangeCircle?: Phaser.GameObjects.Graphics
  arm: Phaser.GameObjects.Container
  fishInHand: Phaser.GameObjects.Graphics
  armRestY: number
  bearSprite?: Phaser.GameObjects.Image
  tubSprite?: Phaser.GameObjects.Image
  currentTier?: number
  upgrades: TowerUpgrades
  levelText?: Phaser.GameObjects.Text
  // Effective stats (base + upgrades)
  effectiveDamage: number
  effectiveRange: number
  effectiveFireRate: number
  effectiveSplashRadius?: number
}

interface Projectile {
  graphics: Phaser.GameObjects.Container
  targetEnemy: number
  tx: number
  ty: number
  damage: number
  speed: number
  special: string
  splashRadius?: number
  slowAmount?: number
  slowDuration?: number
  chainCount?: number
  pierced: Set<number>
  alive: boolean
  // Grenade arc fields
  isGrenade?: boolean
  startX?: number
  startY?: number
  totalDist?: number
  traveled?: number
  arcHeight?: number
  // Boomerang fields
  isBoomerang?: boolean
  returning?: boolean
  originX?: number
  originY?: number
  boomerangPierced?: Set<number> // separate set for return trip
}

interface PathRider {
  graphics: Phaser.GameObjects.Container
  damage: number
  speed: number
  pathIndex: number // current segment on path, moves FORWARD toward enemies
  x: number
  y: number
  alive: boolean
  towerDef: TowerDef
}

export class GameScene extends Phaser.Scene {
  private enemies: Enemy[] = []
  private towers: Tower[] = []
  private projectiles: Projectile[] = []
  private riders: PathRider[] = []
  private coins = 100
  private lives = 20
  private wave = 0
  private waveActive = false
  private enemiesToSpawn = 0
  private enemiesSpawned = 0
  private spawnTimer = 0
  private spawnInterval = 1000
  private currentWaveHp = 20
  private currentWaveSpeed = 50
  private currentWaveReward = 5
  private currentWaveType = 'basic'
  private betweenWaves = true
  private betweenWaveTimer = 0
  private testingMode = false

  private selectedTower: string | null = null
  private placementValid = false
  private placementGhost: Phaser.GameObjects.Container | null = null
  private placementConfirmed = false // true when bear has been positioned, waiting for confirm
  private placementPos: { x: number; y: number } | null = null

  // UI
  private coinText!: Phaser.GameObjects.Text
  private livesText!: Phaser.GameObjects.Text
  private waveText!: Phaser.GameObjects.Text
  private infoText!: Phaser.GameObjects.Text
  private startWaveBtn!: Phaser.GameObjects.Container
  private startWaveBtnBg!: Phaser.GameObjects.Rectangle
  private startWaveBtnText!: Phaser.GameObjects.Text
  private confirmBtn!: Phaser.GameObjects.Container
  private cancelBtn!: Phaser.GameObjects.Container

  // Tower panel buttons (for highlight/disable)
  private towerButtons: { key: string; bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text; costText: Phaser.GameObjects.Text }[] = []

  // Upgrade system
  private selectedUpgradeTower: Tower | null = null
  private upgradePanel: Phaser.GameObjects.Container | null = null

  // Mobile
  private isMobile = false
  private portraitOverlay: Phaser.GameObjects.Container | null = null
  private leftNav!: Phaser.GameObjects.Container
  private leftNavOpen = false
  private navToggleBtn!: Phaser.GameObjects.Container
  private navPage = 0
  private pinchStartDist = 0
  private pinchStartZoom = 1

  // Path (resolved per device)
  private activePath: [number, number][] = PATH_POINTS
  private activePathWidth = PATH_WIDTH

  // Path graphics
  private pathGraphics!: Phaser.GameObjects.Graphics

  // Sound
  private sfxEnabled = true

  constructor() {
    super({ key: 'GameScene' })
  }

  create() {
    this.enemies = []
    this.towers = []
    this.projectiles = []
    this.riders = []
    this.testingMode = !!(this.scene.settings.data as any)?.testingMode
    this.coins = this.testingMode ? 1000000 : 100
    this.lives = 20
    this.wave = 0
    this.waveActive = false
    this.selectedTower = null
    this.placementConfirmed = false
    this.placementPos = null
    this.betweenWaves = true
    this.betweenWaveTimer = 0
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    this.activePath = this.isMobile ? MOBILE_PATH_POINTS : PATH_POINTS
    this.activePathWidth = this.isMobile ? MOBILE_PATH_WIDTH : PATH_WIDTH
    this.towerButtons = []
    this.selectedUpgradeTower = null
    this.upgradePanel = null
    this.navPage = 0
    this.leftNavOpen = false

    this.drawMap()
    this.createUI()

    if (this.isMobile) {
      this.createMobileNav()
      this.createPortraitOverlay()
    } else {
      this.createTowerPanel()
    }

    this.createPlacementButtons()

    // Load saved game if continuing
    const loadSave = !!(this.scene.settings.data as any)?.loadSave
    if (loadSave) {
      this.loadSavedGame()
    }

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onPointerMove(p))
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onPointerDown(p))

    // Escape cancels placement
    this.input.keyboard?.on('keydown-ESC', () => this.cancelPlacement())

    // Mobile pinch-to-zoom
    if (this.isMobile) {
      this.input.addPointer(1) // enable 2nd pointer
      this.input.on('pointerdown', () => {
        const p1 = this.input.pointer1
        const p2 = this.input.pointer2
        if (p1.isDown && p2.isDown) {
          this.pinchStartDist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y)
          this.pinchStartZoom = this.cameras.main.zoom
        }
      })
      this.input.on('pointermove', () => {
        const p1 = this.input.pointer1
        const p2 = this.input.pointer2
        if (p1.isDown && p2.isDown && this.pinchStartDist > 0) {
          const currentDist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y)
          const scale = currentDist / this.pinchStartDist
          const newZoom = Phaser.Math.Clamp(this.pinchStartZoom * scale, 1.0, 3.0)
          this.cameras.main.setZoom(newZoom)
          if (newZoom <= 1.01) {
            this.cameras.main.scrollX = 0
            this.cameras.main.scrollY = 0
          }
        }
      })
      this.input.on('pointerup', () => {
        this.pinchStartDist = 0
      })

      // Drag to pan when zoomed in (single finger, not placing tower)
      let dragPrevX = 0, dragPrevY = 0
      this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
        if (!this.input.pointer2.isDown && this.cameras.main.zoom > 1.05 && !this.selectedTower) {
          dragPrevX = p.x
          dragPrevY = p.y
        }
      })
      this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (p.isDown && !this.input.pointer2.isDown && this.cameras.main.zoom > 1.05 && !this.selectedTower && this.pinchStartDist === 0) {
          const cam = this.cameras.main
          const dx = (dragPrevX - p.x) / cam.zoom
          const dy = (dragPrevY - p.y) / cam.zoom
          const maxScrollX = (GAME_WIDTH * (cam.zoom - 1)) / (2 * cam.zoom)
          const maxScrollY = (GAME_HEIGHT * (cam.zoom - 1)) / (2 * cam.zoom)
          cam.scrollX = Phaser.Math.Clamp(cam.scrollX + dx, -maxScrollX, maxScrollX)
          cam.scrollY = Phaser.Math.Clamp(cam.scrollY + dy, -maxScrollY, maxScrollY)
          dragPrevX = p.x
          dragPrevY = p.y
        }
      })
    }

    // Orientation change detection
    this.scale.on('resize', () => this.checkOrientation())
  }

  playSfx(key: string, volume = 0.5) {
    if (!this.sfxEnabled) return
    try {
      this.sound.play(`sfx-${key}`, { volume })
    } catch (_) { /* ignore if sound not loaded */ }
  }

  drawMap() {
    const W = GAME_WIDTH, H = GAME_HEIGHT

    // === GROUND TEXTURE (full snow field) ===
    const groundG = this.add.graphics().setDepth(2)
    groundG.fillStyle(0xE8EEF2)
    groundG.fillRect(0, 0, W, H)

    // Snow drifts (layered, organic shapes)
    groundG.fillStyle(0xffffff, 0.35)
    for (let i = 0; i < 30; i++) {
      const sx = Math.random() * W
      const sy = 110 + Math.random() * (H - 200)
      const rx = 15 + Math.random() * 25
      const ry = 8 + Math.random() * 12
      groundG.fillEllipse(sx, sy, rx, ry)
    }

    // Subtle ice sparkles
    groundG.fillStyle(0xE3F2FD, 0.6)
    for (let i = 0; i < 40; i++) {
      const sx = Math.random() * W
      const sy = 100 + Math.random() * (H - 180)
      groundG.fillCircle(sx, sy, 1 + Math.random() * 2)
    }

    // === TREES (improved with snow-covered layers) ===
    // Trees — placed away from path, consistent sizing
    const treeSpots: [number, number, number][] = [
      // [x, y, sizeVariant 0=small 1=medium 2=large]
      [25, 35, 0], [870, 55, 1], [850, 170, 0],
      [20, 200, 1], [880, 320, 0], [30, 490, 0],
      [150, 540, 1], [350, 560, 0], [500, 550, 2],
      [700, 560, 1], [820, 480, 0], [450, 30, 0],
      [650, 30, 1],
    ]
    // Filter out any trees too close to the path
    const safeTrees = treeSpots.filter(([tx, ty]) => {
      for (let i = 0; i < this.activePath.length - 1; i++) {
        const d = this.pointToSegmentDist(tx, ty, this.activePath[i][0], this.activePath[i][1], this.activePath[i + 1][0], this.activePath[i + 1][1])
        if (d < this.activePathWidth + 30) return false
      }
      return true
    })
    for (const [tx, ty, size] of safeTrees) {
      this.drawTree(tx, ty, size)
    }

    // === PATH (improved with gravel texture) ===
    this.pathGraphics = this.add.graphics().setDepth(3)

    // Path shadow
    this.pathGraphics.lineStyle(this.activePathWidth + 6, 0x8D6E63, 0.3)
    this.pathGraphics.beginPath()
    this.pathGraphics.moveTo(this.activePath[0][0], this.activePath[0][1] + 2)
    for (let i = 1; i < this.activePath.length; i++) {
      this.pathGraphics.lineTo(this.activePath[i][0], this.activePath[i][1] + 2)
    }
    this.pathGraphics.strokePath()

    // Main path
    this.pathGraphics.lineStyle(this.activePathWidth, 0xBCAAA4, 0.9)
    this.pathGraphics.beginPath()
    this.pathGraphics.moveTo(this.activePath[0][0], this.activePath[0][1])
    for (let i = 1; i < this.activePath.length; i++) {
      this.pathGraphics.lineTo(this.activePath[i][0], this.activePath[i][1])
    }
    this.pathGraphics.strokePath()

    // Path inner highlight (lighter center)
    this.pathGraphics.lineStyle(this.activePathWidth * 0.5, 0xD7CCC8, 0.4)
    this.pathGraphics.beginPath()
    this.pathGraphics.moveTo(this.activePath[0][0], this.activePath[0][1])
    for (let i = 1; i < this.activePath.length; i++) {
      this.pathGraphics.lineTo(this.activePath[i][0], this.activePath[i][1])
    }
    this.pathGraphics.strokePath()

    // Path border edges
    this.pathGraphics.lineStyle(2, 0xA1887F, 0.5)
    this.pathGraphics.beginPath()
    this.pathGraphics.moveTo(this.activePath[0][0], this.activePath[0][1])
    for (let i = 1; i < this.activePath.length; i++) {
      this.pathGraphics.lineTo(this.activePath[i][0], this.activePath[i][1])
    }
    this.pathGraphics.strokePath()

    // Gravel/stone dots along path
    const gravelG = this.add.graphics().setDepth(4)
    for (let i = 0; i < this.activePath.length - 1; i++) {
      const [ax, ay] = this.activePath[i]
      const [bx, by] = this.activePath[i + 1]
      const segLen = Phaser.Math.Distance.Between(ax, ay, bx, by)
      const dots = Math.floor(segLen / 8)
      for (let d = 0; d < dots; d++) {
        const t = d / dots
        const px = ax + (bx - ax) * t + (Math.random() - 0.5) * (this.activePathWidth * 0.6)
        const py = ay + (by - ay) * t + (Math.random() - 0.5) * (this.activePathWidth * 0.6)
        const shade = Math.random() > 0.5 ? 0xA1887F : 0x8D6E63
        gravelG.fillStyle(shade, 0.2 + Math.random() * 0.2)
        gravelG.fillCircle(px, py, 1 + Math.random() * 1.5)
      }
    }

    // === LODGE (improved) ===
    const lastPt = this.activePath[this.activePath.length - 1]
    const lx = Math.min(lastPt[0], W - 50), ly = Math.min(lastPt[1] - 20, H - 80)
    this.drawLodge(lx, ly)

    // === FALLING SNOW PARTICLES ===
    this.createSnowfall()
  }

  drawTree(x: number, y: number, sizeVariant: number) {
    const treeVariants = [ENVIRONMENT_SPRITES.TREE_1, ENVIRONMENT_SPRITES.TREE_2, ENVIRONMENT_SPRITES.TREE_3]
    const treeSprite = treeVariants[sizeVariant % 3]
    // Fixed scales: small=0.08, medium=0.1, large=0.13
    const scales = [0.08, 0.1, 0.13]
    const tree = this.add.image(x, y, treeSprite)
    tree.setScale(scales[sizeVariant])
    tree.setDepth(5)
  }

  drawLodge(lx: number, ly: number) {
    // Use lodge sprite instead of graphics
    const lodgeSprite = this.add.image(lx, ly, ENVIRONMENT_SPRITES.LODGE)
    lodgeSprite.setScale(0.3) // Adjust scale as needed
    lodgeSprite.setDepth(8)
  }

  createSnowfall() {
    const W = GAME_WIDTH, H = GAME_HEIGHT
    // Create falling snow particles using Phaser graphics
    for (let i = 0; i < 35; i++) {
      const size = 1 + Math.random() * 2.5
      const snow = this.add.circle(
        Math.random() * W,
        Math.random() * H,
        size,
        0xffffff,
        0.4 + Math.random() * 0.4
      ).setDepth(200)

      const duration = 4000 + Math.random() * 6000
      const drift = 20 + Math.random() * 40

      this.tweens.add({
        targets: snow,
        y: H + 10,
        x: snow.x + (Math.random() - 0.5) * drift * 2,
        duration,
        repeat: -1,
        delay: Math.random() * duration,
        onRepeat: () => {
          snow.y = -10
          snow.x = Math.random() * W
        },
      })

      // Gentle sway
      this.tweens.add({
        targets: snow,
        x: `+=${drift}`,
        duration: 2000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      })
    }
  }

  createUI() {
    const W = GAME_WIDTH, H = GAME_HEIGHT
    
    // Mobile safe zones: top 100px and bottom 50px are hidden
    const topHudY = this.isMobile ? 115 : 15  // Move HUD down for mobile
    const infoY = this.isMobile ? H - 70 : H - 90  // Move info text up for mobile
    
    // Add decorative bars for mobile safe zones (hidden areas)
    if (this.isMobile) {
      // Top decorative banner (wooden plank style) - hidden zone
      const topBanner = this.add.rectangle(W / 2, 50, W, 100, 0x8D6E63, 0.8).setDepth(1)
      this.add.rectangle(W / 2, 50, W, 100).setStrokeStyle(4, 0x5D4037).setDepth(1)
      // Add wood grain texture effect
      for (let i = 0; i < 8; i++) {
        const lineY = 10 + i * 10
        this.add.rectangle(W / 2, lineY, W * 0.95, 2, 0x5D4037, 0.3).setDepth(2)
      }
      
      // Bottom decorative bar - hidden zone
      const bottomBar = this.add.rectangle(W / 2, H - 25, W, 50, 0x3E2723, 0.8).setDepth(1) 
      this.add.rectangle(W / 2, H - 25, W, 50).setStrokeStyle(2, 0x2E1B1B).setDepth(1)
    }
    
    // Top bar background (positioned below safe zone for mobile)
    this.add.rectangle(W / 2, topHudY, W, 30, 0x37474F, 0.85).setDepth(90)

    this.coinText = this.add.text(20, topHudY, '🐟 100', {
      fontSize: '16px', fontFamily: 'Arial', color: '#64B5F6', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(100)

    this.livesText = this.add.text(150, topHudY, '❤️ 20', {
      fontSize: '16px', fontFamily: 'Arial', color: '#EF9A9A', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(100)

    this.waveText = this.add.text(W / 2, topHudY, 'Wave 0', {
      fontSize: '16px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(100)

    this.infoText = this.add.text(W / 2, infoY, '', {
      fontSize: '14px', fontFamily: 'Arial', color: '#5D4037', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(100)

    // Start wave button (positioned below safe zone for mobile)
    this.startWaveBtnBg = this.add.rectangle(0, 0, 160, 36, 0x4CAF50).setInteractive({ useHandCursor: true })
    this.startWaveBtnText = this.add.text(0, 0, '▶ Start Wave 1', {
      fontSize: '14px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5)

    this.startWaveBtn = this.add.container(W - 100, topHudY, [this.startWaveBtnBg, this.startWaveBtnText]).setDepth(100)

    this.startWaveBtnBg.on('pointerdown', () => {
      if (!this.waveActive) this.startNextWave()
    })
    this.startWaveBtnBg.on('pointerover', () => {
      if (!this.waveActive) this.startWaveBtnBg.setFillStyle(0x66BB6A)
    })
    this.startWaveBtnBg.on('pointerout', () => {
      if (!this.waveActive) this.startWaveBtnBg.setFillStyle(0x4CAF50)
    })

    // Menu / Start Over button (top-left area, after coins/lives)
    const menuBtnBg = this.add.rectangle(0, 0, 70, 24, 0x5D4037, 0.8).setInteractive({ useHandCursor: true })
    const menuBtnText = this.add.text(0, 0, '🏠 Menu', {
      fontSize: '11px', fontFamily: 'Arial', color: '#fff',
    }).setOrigin(0.5)
    const menuBtn = this.add.container(W - 250, topHudY, [menuBtnBg, menuBtnText]).setDepth(100)
    
    menuBtnBg.on('pointerover', () => menuBtnBg.setFillStyle(0x795548, 0.9))
    menuBtnBg.on('pointerout', () => menuBtnBg.setFillStyle(0x5D4037, 0.8))
    menuBtnBg.on('pointerdown', () => {
      this.saveGame()
      this.scene.start('BootScene')
    })
  }

  createTowerPanel() {
    const W = GAME_WIDTH, H = GAME_HEIGHT
    // Position panel above mobile safe zone (50px from bottom) for mobile, normal for desktop
    const panelY = this.isMobile ? H - 80 : H - 30
    // Bottom panel
    this.add.rectangle(W / 2, panelY, W, 60, 0x3E2723, 0.9).setDepth(90)

    const towerCount = TOWER_ORDER.length
    const totalPadding = 16 // 8px each side
    const gap = 3
    const w = Math.floor((W - totalPadding - gap * (towerCount - 1)) / towerCount)
    let xPos = 8
    for (const key of TOWER_ORDER) {
      const def = TOWERS[key]

      const bg = this.add.rectangle(xPos + w / 2, panelY, w, 50, 0x5D4037, 0.9).setDepth(95).setInteractive({ useHandCursor: true })
      this.add.rectangle(xPos + w / 2, panelY, w, 50).setStrokeStyle(1, 0x8D6E63).setDepth(95)

      const nameText = this.add.text(xPos + w / 2, panelY - 12, `${def.emoji} ${def.name.split(' ')[0]}`, {
        fontSize: '12px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(100)

      const costText = this.add.text(xPos + w / 2, panelY + 10, `🐟${def.cost}`, {
        fontSize: '11px', fontFamily: 'Arial', color: '#90CAF9',
      }).setOrigin(0.5).setDepth(100)

      this.towerButtons.push({ key, bg, text: nameText, costText })

      bg.on('pointerdown', () => this.selectTowerForPlacement(key))

      bg.on('pointerover', () => {
        if (!this.selectedTower || this.selectedTower === key) {
          bg.setFillStyle(0x795548, 0.9)
        }
        this.infoText.setText(`${def.emoji} ${def.name}: ${def.description}`)
      })
      bg.on('pointerout', () => {
        if (this.selectedTower !== key) {
          bg.setFillStyle(0x5D4037, 0.9)
        }
      })

      xPos += w + gap
    }
  }

  selectTowerForPlacement(key: string) {
    if (this.selectedTower) return // Already in placement mode
    this.closeUpgradePanel()
    const def = TOWERS[key]
    if (this.coins < def.cost) {
      this.infoText.setText(`Not enough fish coins! Need 🐟${def.cost}`)
      return
    }

    this.selectedTower = key
    this.placementConfirmed = false
    this.placementPos = null
    this.infoText.setText(`${def.emoji} ${def.name}: Click map to position, then ✓ to confirm`)

    // Highlight selected button, disable others
    this.updateTowerButtonStates()

    // Disable start wave button
    this.startWaveBtnBg.setFillStyle(0x78909C)
    this.startWaveBtnBg.disableInteractive()
    this.startWaveBtnText.setText('🔒 Placing...')

    // Show confirm/cancel buttons
    this.confirmBtn.setVisible(true)
    this.cancelBtn.setVisible(true)

    // Auto-collapse mobile nav
    if (this.isMobile && this.leftNavOpen) {
      this.toggleMobileNav()
    }
  }

  cancelPlacement() {
    if (!this.selectedTower) return

    this.selectedTower = null
    this.placementConfirmed = false
    this.placementPos = null

    if (this.placementGhost) {
      this.placementGhost.destroy()
      this.placementGhost = null
    }

    this.infoText.setText('Placement cancelled.')
    this.updateTowerButtonStates()

    // Re-enable start wave button
    if (!this.waveActive) {
      this.startWaveBtnBg.setFillStyle(0x4CAF50)
      this.startWaveBtnBg.setInteractive({ useHandCursor: true })
      this.startWaveBtnText.setText(`▶ Start Wave ${this.wave + 1}`)
    }

    this.confirmBtn.setVisible(false)
    this.cancelBtn.setVisible(false)
  }

  confirmPlacement() {
    if (!this.selectedTower || !this.placementPos) return

    this.placeTower(this.placementPos.x, this.placementPos.y, this.selectedTower)

    // Clean up
    this.selectedTower = null
    this.placementConfirmed = false
    this.placementPos = null

    if (this.placementGhost) {
      this.placementGhost.destroy()
      this.placementGhost = null
    }

    this.updateTowerButtonStates()

    // Re-enable start wave button
    if (!this.waveActive) {
      this.startWaveBtnBg.setFillStyle(0x4CAF50)
      this.startWaveBtnBg.setInteractive({ useHandCursor: true })
      this.startWaveBtnText.setText(`▶ Start Wave ${this.wave + 1}`)
    }

    this.confirmBtn.setVisible(false)
    this.cancelBtn.setVisible(false)
  }

  updateTowerButtonStates() {
    for (const btn of this.towerButtons) {
      if (this.selectedTower) {
        if (btn.key === this.selectedTower) {
          // Highlight selected
          btn.bg.setFillStyle(0x4CAF50, 0.9)
        } else {
          // Dim/disable others
          btn.bg.setFillStyle(0x3E2723, 0.5)
          btn.bg.disableInteractive()
        }
      } else {
        // Reset all to normal
        btn.bg.setFillStyle(0x5D4037, 0.9)
        btn.bg.setInteractive({ useHandCursor: true })
      }
    }
  }

  createPlacementButtons() {
    // Position buttons above mobile safe zone for mobile
    const buttonY = this.isMobile ? GAME_HEIGHT - 120 : GAME_HEIGHT - 70
    
    // Green checkmark confirm button
    const confirmBg = this.add.rectangle(0, 0, 50, 50, 0x4CAF50).setInteractive({ useHandCursor: true })
    this.add.rectangle(0, 0, 50, 50).setStrokeStyle(2, 0x388E3C)
    const confirmText = this.add.text(0, 0, '✓', {
      fontSize: '28px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5)

    this.confirmBtn = this.add.container(GAME_WIDTH - 60, buttonY, [confirmBg, confirmText]).setDepth(200).setVisible(false)

    confirmBg.on('pointerdown', () => this.confirmPlacement())
    confirmBg.on('pointerover', () => confirmBg.setFillStyle(0x66BB6A))
    confirmBg.on('pointerout', () => confirmBg.setFillStyle(0x4CAF50))

    // Red X cancel button
    const cancelBg = this.add.rectangle(0, 0, 50, 50, 0xEF5350).setInteractive({ useHandCursor: true })
    this.add.rectangle(0, 0, 50, 50).setStrokeStyle(2, 0xC62828)
    const cancelText = this.add.text(0, 0, '✕', {
      fontSize: '28px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5)

    this.cancelBtn = this.add.container(GAME_WIDTH - 120, buttonY, [cancelBg, cancelText]).setDepth(200).setVisible(false)

    cancelBg.on('pointerdown', () => this.cancelPlacement())
    cancelBg.on('pointerover', () => cancelBg.setFillStyle(0xE57373))
    cancelBg.on('pointerout', () => cancelBg.setFillStyle(0xEF5350))
  }

  // === MOBILE SUPPORT ===

  createPortraitOverlay() {
    this.portraitOverlay = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setDepth(500)

    const bg = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x1a1a2e, 0.95)
    const icon = this.add.text(0, -60, '📱↔️', { fontSize: '48px' }).setOrigin(0.5)
    const msg = this.add.text(0, 10, 'Please rotate to\nlandscape mode!', {
      fontSize: '28px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold', align: 'center',
    }).setOrigin(0.5)
    const sub = this.add.text(0, 70, 'Hot Tub Defense plays best in widescreen', {
      fontSize: '14px', fontFamily: 'Arial', color: '#B0BEC5', fontStyle: 'italic',
    }).setOrigin(0.5)

    this.portraitOverlay.add([bg, icon, msg, sub])
    this.checkOrientation()
  }

  checkOrientation() {
    if (!this.isMobile || !this.portraitOverlay) return
    const isPortrait = window.innerHeight > window.innerWidth
    this.portraitOverlay.setVisible(isPortrait)
  }

  createMobileNav() {
    const NAV_W = 300
    const H = GAME_HEIGHT

    // Collapsible left nav panel
    this.leftNav = this.add.container(-NAV_W, 0).setDepth(150)

    // Nav background
    const navBg = this.add.rectangle(NAV_W / 2, H / 2, NAV_W, H, 0x3E2723, 0.95)
    this.leftNav.add(navBg)

    // Scrollable content container
    const scrollContent = this.add.container(0, 0)
    this.leftNav.add(scrollContent)

    // Title
    scrollContent.add(this.add.text(NAV_W / 2, 20, '🐻 Bears', {
      fontSize: '22px', fontFamily: 'Arial', color: '#FFD54F', fontStyle: 'bold',
    }).setOrigin(0.5))

    let yPos = 55
    for (const key of TOWER_ORDER) {
      const def = TOWERS[key]

      const itemBg = this.add.rectangle(NAV_W / 2, yPos + 45, NAV_W - 20, 105, 0x5D4037, 0.8).setInteractive({ useHandCursor: true })
      scrollContent.add(itemBg)

      // Tower name
      scrollContent.add(this.add.text(NAV_W / 2, yPos + 10, `${def.emoji} ${def.name}`, {
        fontSize: '18px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
      }).setOrigin(0.5))

      // Cost
      scrollContent.add(this.add.text(NAV_W / 2, yPos + 35, `🐟 ${def.cost}`, {
        fontSize: '15px', fontFamily: 'Arial', color: '#90CAF9', fontStyle: 'bold',
      }).setOrigin(0.5))

      // Description
      scrollContent.add(this.add.text(NAV_W / 2, yPos + 60, def.description, {
        fontSize: '12px', fontFamily: 'Arial', color: '#B0BEC5', wordWrap: { width: NAV_W - 40 }, align: 'center',
      }).setOrigin(0.5))

      // Mini bear with headband
      const previewG = this.add.graphics()
      previewG.fillStyle(0x8D6E63)
      previewG.fillCircle(NAV_W / 2, yPos + 85, 8)
      previewG.fillCircle(NAV_W / 2 - 6, yPos + 79, 3)
      previewG.fillCircle(NAV_W / 2 + 6, yPos + 79, 3)
      previewG.fillStyle(def.bearColor, 0.9)
      previewG.fillRoundedRect(NAV_W / 2 - 7, yPos + 73, 14, 3, 1)
      previewG.fillStyle(0x3E2723)
      previewG.fillCircle(NAV_W / 2 - 3, yPos + 83, 1.2)
      previewG.fillCircle(NAV_W / 2 + 3, yPos + 83, 1.2)
      scrollContent.add(previewG)

      itemBg.on('pointerdown', () => this.selectTowerForPlacement(key))

      yPos += 115
    }

    // Enable drag-to-scroll on the scroll content
    const totalHeight = yPos + 20
    const visibleHeight = H
    let scrollY = 0

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.leftNavOpen || !pointer.isDown) return
      if (pointer.x > NAV_W) return // only scroll inside nav

      const dy = pointer.y - pointer.prevPosition.y
      scrollY = Phaser.Math.Clamp(scrollY + dy, -(totalHeight - visibleHeight), 0)
      scrollContent.y = scrollY
    })

    // Toggle button (position within mobile safe zone)
    const toggleBg = this.add.rectangle(0, 0, 44, 90, 0x5D4037, 0.9).setInteractive({ useHandCursor: true })
    const toggleText = this.add.text(0, 0, '🐻', { fontSize: '24px' }).setOrigin(0.5)
    // Position toggle button within the playable area (between y=100 and y=670 for mobile)
    const toggleY = H / 2  // Keep at middle, it should be fine between safe zones
    this.navToggleBtn = this.add.container(22, toggleY, [toggleBg, toggleText]).setDepth(151)

    toggleBg.on('pointerdown', () => this.toggleMobileNav())

    this.leftNavOpen = false
  }

  toggleMobileNav() {
    const NAV_W = 300
    this.leftNavOpen = !this.leftNavOpen
    this.tweens.add({
      targets: this.leftNav,
      x: this.leftNavOpen ? 0 : -NAV_W,
      duration: 200,
      ease: 'Power2',
    })
    this.tweens.add({
      targets: this.navToggleBtn,
      x: this.leftNavOpen ? NAV_W + 22 : 22,
      duration: 200,
      ease: 'Power2',
    })
  }

  onPointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.selectedTower) {
      if (this.placementGhost) {
        this.placementGhost.destroy()
        this.placementGhost = null
      }
      return
    }

    // If placement is confirmed (clicked a spot), ghost stays put — don't follow cursor
    if (this.placementPos) return

    const x = pointer.x, y = pointer.y
    this.placementValid = this.isValidPlacement(x, y)

    const def = TOWERS[this.selectedTower]

    // Destroy and rebuild ghost each frame
    if (this.placementGhost) {
      this.placementGhost.destroy()
      this.placementGhost = null
    }

    this.buildPlacementGhost(x, y, def, this.placementValid)
  }

  buildPlacementGhost(x: number, y: number, def: TowerDef, valid: boolean) {
    this.placementGhost = this.add.container(x, y).setDepth(80).setAlpha(valid ? 0.6 : 0.3)
    this.placementGhost.setScale(1.0)

    // Range circle
    const rangeG = this.add.graphics()
    rangeG.fillStyle(valid ? 0x66BB6A : 0xEF5350, 0.15)
    rangeG.fillCircle(0, 0, def.range)
    rangeG.lineStyle(1, valid ? 0x66BB6A : 0xEF5350, 0.4)
    rangeG.strokeCircle(0, 0, def.range)
    this.placementGhost.add(rangeG)

    // === GHOST SPRITES ===
    const towerType = def.key as TowerType
    const sprites = TOWER_SPRITES[towerType]
    
    // Tub sprite (normalized scale)
    const tubScale = TUB_SCALES[def.key] || 0.15
    const tubSprite = this.add.image(0, 0, sprites.tub)
      .setOrigin(0.5, 0.5)
      .setScale(tubScale)
      .setTint(valid ? 0xffffff : 0xff9999)
    this.placementGhost.add(tubSprite)

    // Bear sprite 
    const bx = -32 // bear position to left of tub
    const bearSprite = this.add.image(bx, -10, sprites.bear)
      .setOrigin(0.5, 0.5)
      .setScale(0.08) // Scale down to match game size
      .setTint(valid ? 0xffffff : 0xff9999) // Tint red if invalid
    this.placementGhost.add(bearSprite)

    // Emoji label removed — sprites handle identification
  }

  onPointerDown(pointer: Phaser.Input.Pointer) {
    // Account for mobile safe zones when checking panel area clicks
    const panelThreshold = this.isMobile ? GAME_HEIGHT - 110 : GAME_HEIGHT - 60
    if (pointer.y > panelThreshold) return // clicking panel area

    // If not in placement mode, check if clicking a placed tower for upgrades
    if (!this.selectedTower) {
      // Check if we clicked on a placed tower
      let clickedTower: Tower | null = null
      for (const tower of this.towers) {
        const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, tower.x, tower.y)
        const hitRadius = 30 * (tower.def.tubScale || 1)
        if (dist < hitRadius) {
          clickedTower = tower
          break
        }
      }

      if (clickedTower) {
        if (this.selectedUpgradeTower === clickedTower) {
          // Clicking same tower again — deselect
          this.closeUpgradePanel()
        } else {
          this.showUpgradePanel(clickedTower)
        }
      } else {
        // Clicked empty space — close upgrade panel
        this.closeUpgradePanel()
      }
      return
    }

    const valid = this.isValidPlacement(pointer.x, pointer.y)

    if (valid) {
      // Position (or reposition) the ghost here
      this.placementPos = { x: pointer.x, y: pointer.y }
      this.placementConfirmed = true

      // Rebuild ghost at the clicked position
      if (this.placementGhost) {
        this.placementGhost.destroy()
        this.placementGhost = null
      }

      const def = TOWERS[this.selectedTower]
      this.buildPlacementGhost(pointer.x, pointer.y, def, true)
      this.infoText.setText('Click ✓ to confirm or click elsewhere to reposition')
    }
  }

  isValidPlacement(x: number, y: number): boolean {
    if (y < 35 || y > GAME_HEIGHT - 65) return false
    if (x < 10 || x > GAME_WIDTH - 10) return false

    const newScale = this.selectedTower ? (TOWERS[this.selectedTower].tubScale || 1) : 1

    // Not on path (bigger towers need more clearance)
    const pathClearance = this.activePathWidth + 10 + (newScale - 1) * 20
    for (let i = 0; i < this.activePath.length - 1; i++) {
      const [ax, ay] = this.activePath[i]
      const [bx, by] = this.activePath[i + 1]
      const dist = this.pointToSegmentDist(x, y, ax, ay, bx, by)
      if (dist < pathClearance) return false
    }

    // Not on other towers (min distance based on both towers' sizes)
    for (const t of this.towers) {
      const existingScale = t.def.tubScale || 1
      const minDist = 30 * (newScale + existingScale) + 10
      if (Phaser.Math.Distance.Between(x, y, t.x, t.y) < minDist) return false
    }

    return true
  }

  pointToSegmentDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax, dy = by - ay
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return Phaser.Math.Distance.Between(px, py, ax, ay)
    let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
    t = Math.max(0, Math.min(1, t))
    return Phaser.Math.Distance.Between(px, py, ax + t * dx, ay + t * dy)
  }

  placeTower(x: number, y: number, key: string) {
    const def = TOWERS[key]
    this.coins -= def.cost
    this.updateUI()

    const container = this.add.container(x, y).setDepth(30)

    // === HOT TUB SPRITE ===
    const towerType = key as TowerType
    const sprites = TOWER_SPRITES[towerType]
    
    // Load and display tub sprite (normalized to honey size)
    const tubScale = TUB_SCALES[key] || 0.15
    const tubSprite = this.add.image(0, -10, sprites.tub)
      .setOrigin(0.5, 0.5)
      .setScale(tubScale)
    container.add(tubSprite)

    // === STEAM SPRITES rising from tub ===
    for (let s = 0; s < 3; s++) {
      const steamSprite = this.add.image(
        -10 + s * 10 + (Math.random() - 0.5) * 8,
        -8,
        ANIMATION_SPRITES.STEAM
      ).setScale(0.8 + Math.random() * 0.4)
      container.add(steamSprite)
      this.tweens.add({
        targets: steamSprite,
        y: -30 - Math.random() * 15,
        x: steamSprite.x + (Math.random() - 0.5) * 10,
        alpha: { from: 0.6, to: 0 },
        scale: steamSprite.scaleX + 1.2,
        duration: 1800 + Math.random() * 1200,
        repeat: -1,
        delay: s * 600 + Math.random() * 500,
      })
    }

    // === WATER RIPPLE SPRITE with pulsing tween ===
    const rippleSprite = this.add.image(0, 4, ANIMATION_SPRITES.RIPPLE)
      .setScale(0.8)
      .setAlpha(0.4)
    container.add(rippleSprite)
    this.tweens.add({
      targets: rippleSprite,
      scale: { from: 0.6, to: 1.2 },
      alpha: { from: 0.6, to: 0.2 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })

    // === SWIMMING FISH / ITEMS IN TUB (animated) ===
    const swimContainer = this.add.container(0, 0)
    container.add(swimContainer)

    if (def.key === 'honey') {
      // Honey jars floating in tub
      for (let i = 0; i < 3; i++) {
        const jar = this.add.graphics()
        jar.fillStyle(0xFFB300)
        jar.fillRoundedRect(-4, -4, 8, 8, 2)
        jar.fillStyle(0xFFF176, 0.6)
        jar.fillCircle(0, -1, 2)
        jar.fillStyle(0x8D6E63)
        jar.fillRect(-3, -5, 6, 2) // lid
        const jarC = this.add.container(-12 + i * 12, 4 + (i % 2) * 6, [jar])
        swimContainer.add(jarC)
        // Bob animation
        this.tweens.add({
          targets: jarC,
          y: jarC.y + 3,
          x: jarC.x + (i % 2 ? 3 : -3),
          duration: 1500 + i * 300,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        })
      }
    } else {
      // Fish swimming in circles in the tub
      const fishCount = def.key === 'blobfish' ? 1 : def.key === 'shark' ? 2 : 3
      for (let i = 0; i < fishCount; i++) {
        const fish = this.add.graphics()
        this.drawSwimmingFish(fish, def)
        const fishC = this.add.container(0, -6, [fish])
        swimContainer.add(fishC)

        // Circular swimming motion
        const angle = (i / fishCount) * Math.PI * 2
        const rx = 12, ry = 6
        this.tweens.addCounter({
          from: angle * 100,
          to: (angle + Math.PI * 2) * 100,
          duration: 3000 + i * 500,
          repeat: -1,
          onUpdate: (tween: Phaser.Tweens.Tween) => {
            const a = (tween.getValue() ?? 0) / 100
            fishC.x = Math.cos(a) * rx
            fishC.y = -6 + Math.sin(a) * ry
            fish.setScale(Math.cos(a) > 0 ? 1 : -1)
          },
        })
      }
    }

    // === BEAR SPRITE ===
    const bx = -32 // bear stands to the left of tub
    
    // Load and display bear sprite
    const bearSprite = this.add.image(bx, -10, sprites.bear)
      .setOrigin(0.5, 0.5)
      .setScale(0.08) // Scale down to match game size
    container.add(bearSprite)

    // Arm is now part of the bear sprite — throw animation swaps texture
    const arm = this.add.container(bx + 10, -4) // keep for compatibility
    const fishG = this.add.graphics() // keep for compatibility
    container.add(arm)
    // Store bear sprite ref for throw animation
    const bearSpriteRef = bearSprite

    // Tower emoji label removed — sprites make it unnecessary

    // Catfish support glow
    if (def.special === 'boost') {
      const glow = this.add.graphics()
      glow.fillStyle(0x80CBC4, 0.12)
      glow.fillCircle(0, 0, def.boostRange || 100)
      container.addAt(glow, 0)
      this.tweens.add({
        targets: glow,
        alpha: 0.4,
        duration: 1500,
        yoyo: true,
        repeat: -1,
      })
    }

    // All towers same visual size (matching honey bear size)
    // tubScale only affects range/placement, not visual
    container.setScale(1.0)

    // Level indicator
    const levelText = this.add.text(x, y - 32 * (def.tubScale || 1), '', {
      fontSize: '10px', fontFamily: 'Arial', color: '#FFD54F', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(32)

    const tower: Tower = {
      container,
      def,
      x, y,
      fireTimer: 0,
      arm,
      fishInHand: fishG,
      armRestY: arm.y,
      upgrades: { damage: 0, range: 0, speed: 0 },
      levelText,
      effectiveDamage: def.damage,
      effectiveRange: def.range,
      effectiveFireRate: def.fireRate,
      effectiveSplashRadius: def.splashRadius,
      bearSprite: bearSpriteRef,
      tubSprite,
      currentTier: 0,
    }

    this.towers.push(tower)
    this.infoText.setText(`${def.emoji} ${def.name} placed!`)
    this.playSfx('place-tower', 0.4)
    this.saveGame()
  }

  drawSwimmingFish(g: Phaser.GameObjects.Graphics, def: TowerDef) {
    const fc = def.projectileColor
    if (def.key === 'shark') {
      g.fillStyle(0x546E7A)
      g.fillTriangle(-6, 0, 6, -3, 6, 3)
      g.fillTriangle(-6, 0, -10, -4, -10, 4)
      g.fillStyle(0x455A64)
      g.fillTriangle(1, -3, 4, -3, 2, -6)
      g.fillStyle(0x263238)
      g.fillCircle(5, -1, 1)
    } else if (def.key === 'pufferfish') {
      g.fillStyle(fc)
      g.fillCircle(0, 0, 4)
      g.lineStyle(1, 0xF9A825)
      for (let a = 0; a < Math.PI * 2; a += 1) {
        g.lineBetween(Math.cos(a) * 3, Math.sin(a) * 3, Math.cos(a) * 5.5, Math.sin(a) * 5.5)
      }
    } else if (def.key === 'swordfish') {
      g.fillStyle(fc)
      g.fillTriangle(-5, 0, 5, -2, 5, 2)
      g.lineStyle(1, 0x42A5F5)
      g.lineBetween(5, 0, 9, 0)
      g.fillTriangle(-5, 0, -8, -3, -8, 3)
    } else if (def.key === 'eel') {
      g.lineStyle(3, fc)
      g.beginPath()
      g.moveTo(-6, 0)
      g.lineTo(-3, -3)
      g.lineTo(0, 1)
      g.lineTo(3, -2)
      g.lineTo(6, 0)
      g.strokePath()
      g.fillStyle(0xFFFF00, 0.7)
      g.fillCircle(5, -2, 1)
    } else if (def.key === 'catfish') {
      g.fillStyle(fc)
      g.fillEllipse(0, 0, 10, 5)
      g.lineStyle(0.8, 0x4DB6AC)
      g.lineBetween(4, -1, 8, -3)
      g.lineBetween(4, 1, 8, 3)
    } else if (def.key === 'bass') {
      g.fillStyle(0x4CAF50)
      g.fillTriangle(-5, 0, 5, -2, 5, 2)
      g.fillStyle(0x388E3C)
      g.fillTriangle(-5, 0, -8, -3, -8, 3)
      g.fillStyle(0x263238)
      g.fillCircle(4, -1, 0.8)
    } else if (def.key === 'blobfish') {
      // Blobfish — droopy sad blob
      g.fillStyle(0xE91E63, 0.8)
      g.fillEllipse(0, 0, 10, 7)
      g.fillStyle(0xF48FB1, 0.6)
      g.fillEllipse(0, 2, 6, 4)
      g.fillStyle(0x263238)
      g.fillCircle(-2, -1, 1)
      g.fillCircle(2, -1, 1)
    } else {
      // Salmon
      g.fillStyle(fc)
      g.fillTriangle(-5, 0, 5, -2, 5, 2)
      g.fillTriangle(-5, 0, -8, -3, -8, 3)
      g.fillStyle(0xE57373)
      g.fillEllipse(2, 1, 4, 2)
      g.fillStyle(0x263238)
      g.fillCircle(4, -1, 0.8)
    }
  }

  drawHeldItem(g: Phaser.GameObjects.Graphics, def: TowerDef, px = 14) {
    g.clear()
    // px = paw x position, items drawn centered around (px, 0)
    const cx = px
    if (def.key === 'honey') {
      g.fillStyle(0xFFB300)
      g.fillCircle(cx, -2, 4)
      g.fillStyle(0xFFF176, 0.6)
      g.fillCircle(cx + 1, -3, 1.5)
      g.fillStyle(0xFFB300, 0.7)
      g.fillEllipse(cx, 3, 2, 3)
    } else if (def.key === 'shark') {
      g.fillStyle(0x546E7A)
      g.fillTriangle(cx - 6, 0, cx + 8, -3, cx + 8, 3)
      g.fillTriangle(cx - 7, 0, cx - 11, -4, cx - 11, 4)
      g.fillStyle(0x455A64)
      g.fillTriangle(cx, -3, cx + 4, -3, cx + 2, -7)
      g.fillStyle(0x263238)
      g.fillCircle(cx + 6, -1, 1)
    } else if (def.key === 'pufferfish') {
      g.fillStyle(0xFFEE58)
      g.fillCircle(cx, -1, 4)
      g.lineStyle(1, 0xF9A825)
      for (let a = 0; a < Math.PI * 2; a += 0.9) {
        g.lineBetween(cx + Math.cos(a) * 3, -1 + Math.sin(a) * 3, cx + Math.cos(a) * 6, -1 + Math.sin(a) * 6)
      }
    } else if (def.key === 'swordfish') {
      g.fillStyle(0x90CAF9)
      g.fillTriangle(cx - 5, 0, cx + 7, -2, cx + 7, 2)
      g.lineStyle(1.5, 0x42A5F5)
      g.lineBetween(cx + 7, 0, cx + 12, 0)
      g.fillTriangle(cx - 5, 0, cx - 8, -3, cx - 8, 3)
    } else if (def.key === 'eel') {
      g.lineStyle(3, 0x7C4DFF)
      g.beginPath()
      g.moveTo(cx - 6, 0)
      g.lineTo(cx - 2, -3)
      g.lineTo(cx + 2, 1)
      g.lineTo(cx + 6, -2)
      g.strokePath()
      g.fillStyle(0xFFFF00, 0.8)
      g.fillCircle(cx + 5, -3, 1.5)
    } else if (def.key === 'catfish') {
      g.fillStyle(0x80CBC4)
      g.fillEllipse(cx, -1, 10, 5)
      g.lineStyle(0.8, 0x4DB6AC)
      g.lineBetween(cx + 4, -2, cx + 8, -4)
      g.lineBetween(cx + 4, 1, cx + 8, 3)
    } else if (def.key === 'bass') {
      g.fillStyle(0x4CAF50)
      g.fillTriangle(cx - 5, 0, cx + 7, -2, cx + 7, 2)
      g.fillStyle(0x388E3C)
      g.fillTriangle(cx - 5, 0, cx - 8, -3, cx - 8, 3)
      g.fillStyle(0x2E7D32)
      g.fillTriangle(cx, -2, cx + 3, -2, cx + 1, -5)
      g.fillStyle(0x263238)
      g.fillCircle(cx + 5, -1, 0.8)
    } else if (def.key === 'blobfish') {
      g.fillStyle(0xE91E63, 0.9)
      g.fillEllipse(cx, -1, 12, 8)
      g.fillStyle(0xF48FB1, 0.7)
      g.fillEllipse(cx, 2, 7, 5)
      g.fillStyle(0x263238)
      g.fillCircle(cx - 2, -2, 1.2)
      g.fillCircle(cx + 2, -2, 1.2)
    } else {
      // Salmon
      g.fillStyle(0xEF9A9A)
      g.fillTriangle(cx - 5, 0, cx + 7, -2, cx + 7, 2)
      g.fillTriangle(cx - 6, 0, cx - 9, -3, cx - 9, 3)
      g.fillStyle(0xE57373)
      g.fillEllipse(cx + 3, 1, 4, 2)
      g.fillStyle(0x263238)
      g.fillCircle(cx + 5, -1, 0.8)
    }
  }

  drawProjectile(g: Phaser.GameObjects.Graphics, def: TowerDef) {
    // All projectiles drawn centered at 0,0, pointing RIGHT (rotation applied by container)
    if (def.key === 'honey') {
      // Honey glob
      g.fillStyle(0xFFB300, 0.9)
      g.fillCircle(0, 0, 5)
      g.fillStyle(0xFFF176, 0.6)
      g.fillCircle(1, -1, 2)
      // Drips
      g.fillStyle(0xFFB300, 0.6)
      g.fillEllipse(0, 5, 3, 4)
    } else if (def.key === 'shark') {
      // Shark! Big and mean
      g.fillStyle(0x546E7A)
      g.fillTriangle(-10, 0, 14, 0, 2, -6) // body top
      g.fillTriangle(-10, 0, 14, 0, 2, 5)  // body bottom
      g.fillStyle(0xCFD8DC)
      g.fillTriangle(-4, 0, 12, 0, 4, 4)   // belly
      // Dorsal fin
      g.fillStyle(0x455A64)
      g.fillTriangle(2, -6, 6, -6, 4, -12)
      // Tail
      g.fillTriangle(-10, 0, -16, -6, -16, 6)
      // Eye
      g.fillStyle(0x263238)
      g.fillCircle(10, -2, 1.5)
      // Teeth
      g.fillStyle(0xffffff)
      g.fillTriangle(13, -1, 15, 0, 13, 1)
    } else if (def.key === 'pufferfish') {
      // Pufferfish — round and spiky
      g.fillStyle(0xFFEE58)
      g.fillCircle(0, 0, 6)
      g.fillStyle(0xFFF9C4)
      g.fillCircle(0, 0, 3)
      // Spikes
      g.lineStyle(1.5, 0xF9A825)
      for (let a = 0; a < Math.PI * 2; a += 0.8) {
        g.lineBetween(Math.cos(a) * 5, Math.sin(a) * 5, Math.cos(a) * 9, Math.sin(a) * 9)
      }
      g.fillStyle(0x3E2723)
      g.fillCircle(3, -2, 1)
    } else if (def.key === 'swordfish') {
      // Swordfish — sleek and pointy
      g.fillStyle(0x64B5F6)
      g.fillTriangle(-8, 0, 8, -4, 8, 4)   // body
      g.fillStyle(0x90CAF9)
      g.fillTriangle(-4, 0, 8, 0, 8, 3)    // belly
      // Sword
      g.lineStyle(2, 0x42A5F5)
      g.lineBetween(8, 0, 16, 0)
      // Tail
      g.fillStyle(0x42A5F5)
      g.fillTriangle(-8, 0, -13, -5, -13, 5)
      // Eye
      g.fillStyle(0x263238)
      g.fillCircle(5, -1, 1.2)
      // Dorsal fin
      g.fillStyle(0x42A5F5)
      g.fillTriangle(0, -4, 4, -4, 2, -8)
    } else if (def.key === 'eel') {
      // Electric eel — wavy with sparks
      g.lineStyle(4, 0x7C4DFF)
      g.beginPath()
      g.moveTo(-10, 0)
      g.lineTo(-5, -4)
      g.lineTo(0, 2)
      g.lineTo(5, -3)
      g.lineTo(10, 0)
      g.strokePath()
      // Sparks
      g.fillStyle(0xFFFF00, 0.9)
      g.fillCircle(8, -4, 2)
      g.fillCircle(-6, 3, 1.5)
      g.fillStyle(0xE1BEE7)
      g.fillCircle(3, -5, 1)
      // Eye
      g.fillStyle(0xffffff)
      g.fillCircle(10, 0, 2)
      g.fillStyle(0x263238)
      g.fillCircle(10, 0, 1)
    } else if (def.key === 'blobfish') {
      // Blobfish grenade — big sad blob with fuse
      g.fillStyle(0xE91E63, 0.9)
      g.fillEllipse(0, 0, 16, 12)
      g.fillStyle(0xF48FB1, 0.7)
      g.fillEllipse(0, 3, 10, 7) // droopy nose/chin
      // Sad eyes
      g.fillStyle(0x263238)
      g.fillCircle(-3, -2, 2)
      g.fillCircle(3, -2, 2)
      // Sad mouth
      g.lineStyle(1.5, 0x880E4F)
      g.beginPath()
      g.arc(0, 4, 4, Math.PI + 0.3, -0.3, false)
      g.strokePath()
      // Fuse spark
      g.fillStyle(0xFFEB3B, 0.9)
      g.fillCircle(0, -8, 3)
      g.fillStyle(0xFF9800, 0.7)
      g.fillCircle(1, -10, 2)
    } else {
      // Salmon (default)
      g.fillStyle(0xEF9A9A)
      g.fillTriangle(-8, 0, 10, -4, 10, 4) // body
      g.fillStyle(0xE57373)
      g.fillTriangle(-4, 0, 8, 0, 8, 3)    // belly stripe
      // Tail
      g.fillStyle(0xEF9A9A)
      g.fillTriangle(-8, 0, -14, -5, -14, 5)
      // Fin
      g.fillStyle(0xE57373, 0.7)
      g.fillTriangle(0, -4, 4, -4, 2, -7)
      // Eye
      g.fillStyle(0x263238)
      g.fillCircle(7, -1, 1.2)
      // Mouth
      g.fillStyle(0xffffff)
      g.fillCircle(10, 0, 0.8)
    }
  }

  // === UPGRADE SYSTEM ===

  getUpgradeCost(tower: Tower, stat: keyof TowerUpgrades): number {
    const level = tower.upgrades[stat]
    if (level >= 3) return Infinity
    const baseCost = Math.floor(tower.def.cost * 0.5)
    return baseCost + baseCost * level // 50%, 100%, 150% of tower cost
  }

  getUpgradeLabel(stat: keyof TowerUpgrades): string {
    return stat === 'damage' ? '⚔️ Damage' : stat === 'range' ? '🎯 Range' : '⚡ Speed'
  }

  getUpgradeValue(tower: Tower, stat: keyof TowerUpgrades): string {
    if (stat === 'damage') return `${tower.effectiveDamage}`
    if (stat === 'range') return `${tower.effectiveRange}`
    return `${(tower.effectiveFireRate / 1000).toFixed(1)}s`
  }

  getUpgradePreview(tower: Tower, stat: keyof TowerUpgrades): string {
    if (tower.upgrades[stat] >= 3) return 'MAX'
    if (stat === 'damage') return `→ ${Math.floor(tower.def.damage * (1 + (tower.upgrades.damage + 1) * 0.25))}`
    if (stat === 'range') return `→ ${Math.floor(tower.def.range * (1 + (tower.upgrades.range + 1) * 0.15))}`
    const newRate = tower.def.fireRate * (1 - (tower.upgrades.speed + 1) * 0.15)
    return `→ ${(newRate / 1000).toFixed(1)}s`
  }

  applyUpgrade(tower: Tower, stat: keyof TowerUpgrades) {
    const cost = this.getUpgradeCost(tower, stat)
    if (this.coins < cost || tower.upgrades[stat] >= 3) return

    this.coins -= cost
    tower.upgrades[stat]++

    // Recalculate effective stats
    tower.effectiveDamage = Math.floor(tower.def.damage * (1 + tower.upgrades.damage * 0.25))
    tower.effectiveRange = Math.floor(tower.def.range * (1 + tower.upgrades.range * 0.15))
    tower.effectiveFireRate = Math.floor(tower.def.fireRate * (1 - tower.upgrades.speed * 0.15))
    if (tower.def.splashRadius) {
      tower.effectiveSplashRadius = Math.floor(tower.def.splashRadius * (1 + tower.upgrades.damage * 0.15))
    }

    // Update level display
    const totalLevel = tower.upgrades.damage + tower.upgrades.range + tower.upgrades.speed
    if (totalLevel > 0) {
      tower.levelText!.setText('⭐'.repeat(Math.min(totalLevel, 9)))
    }

    // Tiered sprite swap (barracuda etc.)
    const tieredDef = TIERED_SPRITES[tower.def.key]
    if (tieredDef) {
      const newTier = getTierIndex(totalLevel)
      if (newTier !== (tower.currentTier ?? 0)) {
        tower.currentTier = newTier
        const tierSprites = tieredDef[newTier]
        if (tower.bearSprite) tower.bearSprite.setTexture(tierSprites.bear)
        if (tower.tubSprite) tower.tubSprite.setTexture(tierSprites.tub)
      }
    }

    this.playSfx('upgrade', 0.4)
    this.saveGame()
    // Upgrade flash effect
    this.tweens.add({
      targets: tower.container,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 150,
      yoyo: true,
      ease: 'Bounce.easeOut',
    })

    // Coin sparkle at tower
    const sparkle = this.add.text(tower.x, tower.y - 40, '✨', { fontSize: '20px' }).setOrigin(0.5).setDepth(200)
    this.tweens.add({
      targets: sparkle, y: sparkle.y - 30, alpha: 0, duration: 600,
      onComplete: () => sparkle.destroy(),
    })

    this.updateUI()
    // Refresh the panel
    this.showUpgradePanel(tower)
  }

  showUpgradePanel(tower: Tower) {
    this.closeUpgradePanel()
    this.selectedUpgradeTower = tower

    // Show range circle on selected tower
    if (tower.rangeCircle) tower.rangeCircle.destroy()
    const rangeG = this.add.graphics().setDepth(29)
    rangeG.fillStyle(0x66BB6A, 0.1)
    rangeG.fillCircle(tower.x, tower.y, tower.effectiveRange)
    rangeG.lineStyle(1, 0x66BB6A, 0.4)
    rangeG.strokeCircle(tower.x, tower.y, tower.effectiveRange)
    tower.rangeCircle = rangeG

    // Panel position — float near tower but stay on screen, account for mobile safe zones
    const panelW = 180, panelH = 170
    let px = tower.x + 50
    let py = tower.y - panelH / 2
    if (px + panelW > GAME_WIDTH - 10) px = tower.x - panelW - 50
    
    // Account for mobile safe zones
    const topBound = this.isMobile ? 105 : 35  // Below mobile top safe zone
    const bottomBound = this.isMobile ? GAME_HEIGHT - 120 : GAME_HEIGHT - 65  // Above mobile bottom safe zone
    
    if (py < topBound) py = topBound
    if (py + panelH > bottomBound) py = bottomBound - panelH

    this.upgradePanel = this.add.container(px, py).setDepth(300)

    // Background
    const bg = this.add.rectangle(panelW / 2, panelH / 2, panelW, panelH, 0x263238, 0.92)
    bg.setStrokeStyle(2, 0x546E7A)
    this.upgradePanel.add(bg)

    // Title
    const totalLvl = tower.upgrades.damage + tower.upgrades.range + tower.upgrades.speed
    const title = this.add.text(panelW / 2, 14, `${tower.def.emoji} ${tower.def.name} Lv${totalLvl}`, {
      fontSize: '12px', fontFamily: 'Arial', color: '#FFD54F', fontStyle: 'bold',
    }).setOrigin(0.5)
    this.upgradePanel.add(title)

    // Upgrade rows
    const stats: (keyof TowerUpgrades)[] = ['damage', 'range', 'speed']
    let rowY = 38

    for (const stat of stats) {
      const level = tower.upgrades[stat]
      const cost = this.getUpgradeCost(tower, stat)
      const maxed = level >= 3
      const canAfford = this.coins >= cost

      // Label + current value
      const label = this.add.text(8, rowY, `${this.getUpgradeLabel(stat)}: ${this.getUpgradeValue(tower, stat)}`, {
        fontSize: '11px', fontFamily: 'Arial', color: '#B0BEC5',
      })
      this.upgradePanel.add(label)

      // Level pips
      for (let i = 0; i < 3; i++) {
        const pip = this.add.rectangle(8 + i * 12, rowY + 18, 8, 6, i < level ? 0x4CAF50 : 0x455A64)
        this.upgradePanel.add(pip)
      }

      // Preview text
      const preview = this.add.text(50, rowY + 15, this.getUpgradePreview(tower, stat), {
        fontSize: '9px', fontFamily: 'Arial', color: maxed ? '#66BB6A' : '#90CAF9',
      })
      this.upgradePanel.add(preview)

      // Upgrade button
      if (!maxed) {
        const btnColor = canAfford ? 0x4CAF50 : 0x616161
        const btnBg = this.add.rectangle(panelW - 30, rowY + 12, 48, 22, btnColor).setInteractive({ useHandCursor: canAfford })
        const btnText = this.add.text(panelW - 30, rowY + 12, `🐟${cost}`, {
          fontSize: '10px', fontFamily: 'Arial', color: canAfford ? '#fff' : '#9E9E9E', fontStyle: 'bold',
        }).setOrigin(0.5)
        this.upgradePanel.add(btnBg)
        this.upgradePanel.add(btnText)

        if (canAfford) {
          btnBg.on('pointerdown', () => this.applyUpgrade(tower, stat))
          btnBg.on('pointerover', () => btnBg.setFillStyle(0x66BB6A))
          btnBg.on('pointerout', () => btnBg.setFillStyle(0x4CAF50))
        }
      } else {
        const maxLabel = this.add.text(panelW - 30, rowY + 12, 'MAX', {
          fontSize: '10px', fontFamily: 'Arial', color: '#66BB6A', fontStyle: 'bold',
        }).setOrigin(0.5)
        this.upgradePanel.add(maxLabel)
      }

      rowY += 40
    }

    // Sell button at bottom
    const sellValue = Math.floor(tower.def.cost * 0.6)
    const sellBg = this.add.rectangle(panelW / 2, panelH - 16, panelW - 16, 22, 0xEF5350).setInteractive({ useHandCursor: true })
    const sellText = this.add.text(panelW / 2, panelH - 16, `🗑️ Sell (🐟${sellValue})`, {
      fontSize: '11px', fontFamily: 'Arial', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5)
    this.upgradePanel.add(sellBg)
    this.upgradePanel.add(sellText)

    sellBg.on('pointerdown', () => {
      this.coins += sellValue
      this.updateUI()
      tower.container.destroy()
      tower.levelText?.destroy()
      tower.rangeCircle?.destroy()
      this.towers = this.towers.filter(t => t !== tower)
      this.closeUpgradePanel()
      this.infoText.setText(`Sold ${tower.def.emoji} ${tower.def.name} for 🐟${sellValue}`)
      this.saveGame()
    })
    sellBg.on('pointerover', () => sellBg.setFillStyle(0xE57373))
    sellBg.on('pointerout', () => sellBg.setFillStyle(0xEF5350))
  }

  closeUpgradePanel() {
    if (this.upgradePanel) {
      this.upgradePanel.destroy()
      this.upgradePanel = null
    }
    if (this.selectedUpgradeTower?.rangeCircle) {
      this.selectedUpgradeTower.rangeCircle.destroy()
      this.selectedUpgradeTower.rangeCircle = undefined
    }
    this.selectedUpgradeTower = null
  }

  startNextWave() {
    this.wave++
    const waveDef = getWave(this.wave)
    this.enemiesToSpawn = waveDef.count
    this.enemiesSpawned = 0
    this.spawnTimer = 0
    this.spawnInterval = waveDef.spacing
    this.currentWaveHp = waveDef.hp
    this.currentWaveSpeed = waveDef.speed
    this.currentWaveReward = waveDef.reward
    this.currentWaveType = waveDef.type
    this.waveActive = true
    this.betweenWaves = false
    this.updateUI()
    this.playSfx('wave-start', 0.5)

    // Disable button during wave
    this.startWaveBtnBg.setFillStyle(0x78909C)
    this.startWaveBtnBg.disableInteractive()
    this.startWaveBtnText.setText(`⚔️ Wave ${this.wave} in progress...`)
  }

  spawnEnemy() {
    const container = this.add.container(this.activePath[0][0], this.activePath[0][1]).setDepth(20)
    const hp = this.currentWaveHp
    const type = this.currentWaveType

    // Use sprite instead of graphics
    let spriteKey: keyof typeof ENEMY_SPRITES = 'BASIC'
    if (type === 'boss') {
      spriteKey = 'BOSS'
    } else if (type === 'fast') {
      spriteKey = 'FAST'
    } else if (type === 'tank') {
      spriteKey = 'TANK'
    }

    const sprite = this.add.image(0, 0, ENEMY_SPRITES[spriteKey])
    const enemyScale = this.isMobile ? 0.035 : 0.05
    const typeMultiplier = type === 'boss' ? 1.4 : type === 'tank' ? 1.2 : type === 'fast' ? 0.8 : 1.0
    sprite.setScale(enemyScale * typeMultiplier)
    container.add(sprite)

    // HP bar
    const hpBar = this.add.graphics()
    container.add(hpBar)

    const enemy: Enemy = {
      graphics: container,
      hp,
      maxHp: hp,
      speed: this.currentWaveSpeed,
      baseSpeed: this.currentWaveSpeed,
      pathIndex: 0,
      pathProgress: 0,
      alive: true,
      type,
      slowTimer: 0,
      hpBar,
    }

    this.enemies.push(enemy)
    this.enemiesSpawned++
    this.drawHpBar(enemy)
  }

  drawHpBar(enemy: Enemy) {
    const g = enemy.hpBar
    g.clear()
    const pct = enemy.hp / enemy.maxHp
    const w = 24
    g.fillStyle(0x000000, 0.4)
    g.fillRect(-w / 2, -30, w, 4)
    const color = pct > 0.6 ? 0x4CAF50 : pct > 0.3 ? 0xFFA726 : 0xEF5350
    g.fillStyle(color)
    g.fillRect(-w / 2, -30, w * pct, 4)
  }

  damageEnemy(enemyIndex: number, damage: number, towerDef: TowerDef) {
    const enemy = this.enemies[enemyIndex]
    if (!enemy || !enemy.alive) return

    // Check catfish boost
    let boostedDmg = damage
    for (const t of this.towers) {
      if (t.def.special === 'boost' && t.def.boostRange) {
        // Check if the attacking tower (any) is in boost range — simplified: boost all
        // Actually we should boost based on tower proximity, but for projectile hits
        // we just check if any catfish is nearby the enemy
        if (Phaser.Math.Distance.Between(t.x, t.y, enemy.graphics.x, enemy.graphics.y) < t.def.boostRange + 50) {
          boostedDmg = Math.floor(damage * (t.def.boostMultiplier || 1.5))
        }
      }
    }

    enemy.hp -= boostedDmg
    this.drawHpBar(enemy)

    // Flash white
    this.tweens.add({
      targets: enemy.graphics,
      alpha: 0.5,
      duration: 50,
      yoyo: true,
    })

    if (enemy.hp <= 0) {
      enemy.alive = false
      this.coins += this.currentWaveReward
      this.updateUI()
      this.playSfx('snowman-death', 0.3)
      this.playSfx('coin', 0.2)

      // Death effect
      const deathText = this.add.text(enemy.graphics.x, enemy.graphics.y - 20, `+🐟${this.currentWaveReward}`, {
        fontSize: '14px', fontFamily: 'Arial', color: '#64B5F6', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(50)

      this.tweens.add({
        targets: deathText,
        y: deathText.y - 30,
        alpha: 0,
        duration: 800,
        onComplete: () => deathText.destroy(),
      })

      // Select explosion sprite based on tower type
      let explosionSprite: string
      switch (towerDef.key) {
        case 'honey':
        case 'pufferfish':
        case 'blobfish':
          explosionSprite = EXPLOSION_SPRITES.FIRE
          break
        case 'eel':
          explosionSprite = EXPLOSION_SPRITES.ELECTRIC
          break
        case 'salmon':
        case 'swordfish':
        case 'shark':
        case 'bass':
        case 'barracuda':
        case 'catfish':
        default:
          explosionSprite = EXPLOSION_SPRITES.ICE
          break
      }

      // Show explosion sprite instead of tween
      const explosionImage = this.add.image(enemy.graphics.x, enemy.graphics.y, explosionSprite)
        .setDepth(45)
        .setScale(0.5)
      
      this.tweens.add({
        targets: explosionImage,
        scale: 1.5,
        alpha: 0,
        duration: 400,
        ease: 'Power2.easeOut',
        onComplete: () => explosionImage.destroy(),
      })

      // Sprite-based melt animation (squash and fade)
      this.tweens.add({
        targets: enemy.graphics,
        scaleY: 0.2,
        scaleX: 1.8,
        alpha: 0,
        duration: 400,
        onComplete: () => enemy.graphics.destroy(),
      })
    }

    // Apply slow
    if (towerDef.special === 'slow' && towerDef.slowAmount && towerDef.slowDuration) {
      enemy.speed = enemy.baseSpeed * (1 - towerDef.slowAmount)
      enemy.slowTimer = towerDef.slowDuration
    }
  }

  updateUI() {
    this.coinText.setText(`🐟 ${this.coins}`)
    this.livesText.setText(`❤️ ${this.lives}`)
    this.waveText.setText(`Wave ${this.wave}`)
  }

  update(_time: number, delta: number) {
    // Spawn enemies
    if (this.waveActive && this.enemiesSpawned < this.enemiesToSpawn) {
      this.spawnTimer += delta
      if (this.spawnTimer >= this.spawnInterval) {
        this.spawnTimer = 0
        this.spawnEnemy()
      }
    }

    // Move enemies
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue

      // Slow timer
      if (enemy.slowTimer > 0) {
        enemy.slowTimer -= delta
        if (enemy.slowTimer <= 0) {
          enemy.speed = enemy.baseSpeed
        }
      }

      // Move along path
      const speed = enemy.speed * (delta / 1000)
      if (enemy.pathIndex < this.activePath.length - 1) {
        const [tx, ty] = this.activePath[enemy.pathIndex + 1]
        const dx = tx - enemy.graphics.x
        const dy = ty - enemy.graphics.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < speed + 2) {
          enemy.pathIndex++
          if (enemy.pathIndex >= this.activePath.length - 1) {
            // Reached the lodge
            enemy.alive = false
            this.lives--
            this.updateUI()
            enemy.graphics.destroy()
            if (this.lives <= 0) {
              GameScene.clearSave()
              this.time.delayedCall(500, () => {
                this.playSfx('game-over', 0.6)
                this.scene.start('GameOverScene', { score: this.coins, wave: this.wave })
              })
              return
            }
            continue
          }
        } else {
          enemy.graphics.x += (dx / dist) * speed
          enemy.graphics.y += (dy / dist) * speed
        }
      }
    }

    // Tower firing
    for (const tower of this.towers) {
      if (tower.def.special === 'boost') continue // Catfish doesn't shoot

      tower.fireTimer += delta
      if (tower.fireTimer >= tower.effectiveFireRate) {
        if (tower.def.special === 'rider') {
          // Bass tower: spawn a rider on the path if enemies exist
          const hasEnemies = this.enemies.some(e => e.alive)
          if (hasEnemies) {
            tower.fireTimer = 0
            this.spawnRider(tower)
          }
        } else {
          // Find target in range
          let targetIdx = -1
          let bestProgress = -1
          for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i]
            if (!e.alive) continue
            const dist = Phaser.Math.Distance.Between(tower.x, tower.y, e.graphics.x, e.graphics.y)
            if (dist <= tower.effectiveRange && e.pathIndex > bestProgress) {
              bestProgress = e.pathIndex
              targetIdx = i
            }
          }

          if (targetIdx >= 0) {
            tower.fireTimer = 0
            this.fireProjectile(tower, targetIdx)
          }
        }
      }
    }

    // Move path riders — seek nearest snowman
    for (const rider of this.riders) {
      if (!rider.alive) continue

      const speed = rider.speed * (delta / 1000)

      // Find nearest alive enemy
      let nearestEnemy: Enemy | null = null
      let nearestDist = Infinity
      for (const e of this.enemies) {
        if (!e.alive) continue
        const d = Phaser.Math.Distance.Between(rider.x, rider.y, e.graphics.x, e.graphics.y)
        if (d < nearestDist) { nearestDist = d; nearestEnemy = e }
      }

      if (nearestEnemy) {
        // Move directly toward nearest enemy
        const dx = nearestEnemy.graphics.x - rider.x
        const dy = nearestEnemy.graphics.y - rider.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist > 1) {
          rider.x += (dx / dist) * speed
          rider.y += (dy / dist) * speed
          rider.graphics.setRotation(Math.atan2(dy, dx))
        }
        rider.graphics.x = rider.x
        rider.graphics.y = rider.y
      } else {
        // No enemies — despawn
        rider.alive = false
        rider.graphics.destroy()
        continue
      }

      // Check collision with enemies
      for (let i = 0; i < this.enemies.length; i++) {
        const e = this.enemies[i]
        if (!e.alive) continue
        const dist = Phaser.Math.Distance.Between(rider.x, rider.y, e.graphics.x, e.graphics.y)
        if (dist < 18) {
          this.damageEnemy(i, rider.damage, rider.towerDef)
          rider.alive = false

          const splash = this.add.circle(rider.x, rider.y, 15, 0x4CAF50, 0.4).setDepth(25)
          this.tweens.add({
            targets: splash, alpha: 0, scale: 2, duration: 300,
            onComplete: () => splash.destroy(),
          })

          rider.graphics.destroy()
          break
        }
      }
    }
    this.riders = this.riders.filter(r => r.alive)

    // Move projectiles
    for (const proj of this.projectiles) {
      if (!proj.alive) continue

      const target = this.enemies[proj.targetEnemy]

      // Update target position if alive (but not for grenades or returning boomerangs)
      if (target && target.alive && !proj.isGrenade && !(proj.isBoomerang && proj.returning)) {
        proj.tx = target.graphics.x
        proj.ty = target.graphics.y
      }

      // Grenade arc movement
      if (proj.isGrenade) {
        const moveSpeed = proj.speed * (delta / 1000)
        proj.traveled! += moveSpeed
        const progress = Math.min(proj.traveled! / proj.totalDist!, 1)

        // Linear interpolation for x/y + parabolic arc for height
        const baseX = proj.startX! + (proj.tx - proj.startX!) * progress
        const baseY = proj.startY! + (proj.ty - proj.startY!) * progress
        const arcOffset = -proj.arcHeight! * Math.sin(progress * Math.PI) // parabolic arc
        proj.graphics.x = baseX
        proj.graphics.y = baseY + arcOffset

        // Spin the blobfish as it flies
        proj.graphics.rotation += 0.08

        // Scale up slightly at peak, down at landing
        const scaleBoost = 1 + 0.3 * Math.sin(progress * Math.PI)
        proj.graphics.setScale(scaleBoost)

        if (progress >= 1) {
          // IMPACT — grenade explosion
          this.playSfx('explosion', 0.5)
          for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i]
            if (!e.alive) continue
            if (Phaser.Math.Distance.Between(proj.tx, proj.ty, e.graphics.x, e.graphics.y) < (proj.splashRadius || 100)) {
              this.damageEnemy(i, proj.damage, TOWERS.blobfish)
            }
          }
          // Big explosion visual
          const splash = this.add.circle(proj.tx, proj.ty, proj.splashRadius || 100, 0xE91E63, 0.4).setDepth(25)
          this.tweens.add({
            targets: splash, alpha: 0, scale: 1.8, duration: 500,
            onComplete: () => splash.destroy(),
          })
          // Shockwave ring
          const ring = this.add.circle(proj.tx, proj.ty, 10, 0xF48FB1, 0).setDepth(26)
          ring.setStrokeStyle(3, 0xE91E63)
          this.tweens.add({
            targets: ring, scaleX: (proj.splashRadius || 100) / 10, scaleY: (proj.splashRadius || 100) / 10,
            alpha: 0, duration: 400, onComplete: () => ring.destroy(),
          })
          proj.alive = false
          proj.graphics.destroy()
          continue
        }
        continue // skip normal movement
      }

      const dx = proj.tx - proj.graphics.x
      const dy = proj.ty - proj.graphics.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      const moveSpeed = proj.speed * (delta / 1000)

      if (dist < moveSpeed + 5) {
        // Hit!
        if (proj.special === 'splash' || proj.special === 'mega') {
          // AoE damage
          for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i]
            if (!e.alive) continue
            if (Phaser.Math.Distance.Between(proj.tx, proj.ty, e.graphics.x, e.graphics.y) < (proj.splashRadius || 60)) {
              this.damageEnemy(i, proj.damage, TOWERS[proj.special === 'mega' ? 'shark' : 'pufferfish'])
            }
          }
          this.playSfx('explosion', 0.3)
          // Splash visual
          const splash = this.add.circle(proj.tx, proj.ty, proj.splashRadius || 60, proj.special === 'mega' ? 0x546E7A : 0xFFEE58, 0.3).setDepth(25)
          this.tweens.add({
            targets: splash,
            alpha: 0,
            scale: 1.5,
            duration: 300,
            onComplete: () => splash.destroy(),
          })
        } else if (proj.special === 'chain' && proj.chainCount && proj.chainCount > 0) {
          this.playSfx('electric', 0.3)
          // Hit current target
          if (target && target.alive) {
            this.damageEnemy(proj.targetEnemy, proj.damage, TOWERS.eel)
          }
          // Chain to next
          let chainCount = proj.chainCount - 1
          let lastX = proj.tx, lastY = proj.ty
          const chained = new Set([proj.targetEnemy])

          for (let c = 0; c < chainCount + 1; c++) {
            let nearIdx = -1, nearDist = 999
            for (let i = 0; i < this.enemies.length; i++) {
              if (!this.enemies[i].alive || chained.has(i)) continue
              const d = Phaser.Math.Distance.Between(lastX, lastY, this.enemies[i].graphics.x, this.enemies[i].graphics.y)
              if (d < 80 && d < nearDist) {
                nearDist = d
                nearIdx = i
              }
            }
            if (nearIdx >= 0) {
              chained.add(nearIdx)
              this.damageEnemy(nearIdx, Math.floor(proj.damage * 0.7), TOWERS.eel)
              // Lightning visual
              const line = this.add.graphics().setDepth(35)
              line.lineStyle(2, 0x7C4DFF, 0.8)
              line.lineBetween(lastX, lastY, this.enemies[nearIdx].graphics.x, this.enemies[nearIdx].graphics.y)
              this.tweens.add({
                targets: line,
                alpha: 0,
                duration: 200,
                onComplete: () => line.destroy(),
              })
              lastX = this.enemies[nearIdx].graphics.x
              lastY = this.enemies[nearIdx].graphics.y
            }
          }
        } else if (proj.special === 'boomerang') {
          // Boomerang: damage on hit, then reverse
          if (!proj.returning) {
            // Outbound hit — damage target and any enemies along the way
            if (target && target.alive) {
              this.damageEnemy(proj.targetEnemy, proj.damage, TOWERS.barracuda)
              proj.pierced.add(proj.targetEnemy)
            }
            // Reverse direction — head back to origin
            proj.returning = true
            proj.boomerangPierced = new Set(proj.pierced)
            proj.tx = proj.originX!
            proj.ty = proj.originY!
          } else {
            // Returned to tower — destroy
            proj.alive = false
            proj.graphics.destroy()
          }
          continue
        } else if (proj.special === 'pierce') {
          // Damage and continue
          if (target && target.alive) {
            this.damageEnemy(proj.targetEnemy, proj.damage, TOWERS.swordfish)
            proj.pierced.add(proj.targetEnemy)
          }
          // Find next enemy in line
          let nextIdx = -1
          for (let i = 0; i < this.enemies.length; i++) {
            if (!this.enemies[i].alive || proj.pierced.has(i)) continue
            const d = Phaser.Math.Distance.Between(proj.graphics.x, proj.graphics.y, this.enemies[i].graphics.x, this.enemies[i].graphics.y)
            if (d < 80) {
              nextIdx = i
              break
            }
          }
          if (nextIdx >= 0 && proj.pierced.size < 3) {
            proj.targetEnemy = nextIdx
            proj.tx = this.enemies[nextIdx].graphics.x
            proj.ty = this.enemies[nextIdx].graphics.y
            return // Don't destroy
          }
        } else {
          // Normal hit
          if (target && target.alive) {
            this.damageEnemy(proj.targetEnemy, proj.damage, TOWERS[proj.special === 'slow' ? 'honey' : 'salmon'])
          }
        }

        proj.alive = false
        proj.graphics.destroy()
      } else {
        proj.graphics.x += (dx / dist) * moveSpeed
        proj.graphics.y += (dy / dist) * moveSpeed
        // Rotate to face target (flip for boomerang return)
        if (proj.isBoomerang && proj.returning) {
          proj.graphics.setRotation(Math.atan2(dy, dx) + Math.PI)
          // Damage enemies along return path
          for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i]
            if (!e.alive || proj.boomerangPierced?.has(i)) continue
            const d = Phaser.Math.Distance.Between(proj.graphics.x, proj.graphics.y, e.graphics.x, e.graphics.y)
            if (d < 20) {
              this.damageEnemy(i, proj.damage, TOWERS.barracuda)
              proj.boomerangPierced?.add(i)
            }
          }
        } else {
          proj.graphics.setRotation(Math.atan2(dy, dx))
        }
        // Boomerang outbound also pierces
        if (proj.isBoomerang && !proj.returning) {
          for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i]
            if (!e.alive || proj.pierced.has(i) || i === proj.targetEnemy) continue
            const d = Phaser.Math.Distance.Between(proj.graphics.x, proj.graphics.y, e.graphics.x, e.graphics.y)
            if (d < 20) {
              this.damageEnemy(i, proj.damage, TOWERS.barracuda)
              proj.pierced.add(i)
            }
          }
        }
      }
    }

    // Clean up dead projectiles
    this.projectiles = this.projectiles.filter(p => p.alive)

    // Check wave complete
    if (this.waveActive && this.enemiesSpawned >= this.enemiesToSpawn) {
      const alive = this.enemies.some(e => e.alive)
      if (!alive) {
        this.waveActive = false
        this.betweenWaves = true
        this.enemies = []
        // Clean up remaining riders
        for (const r of this.riders) { if (r.alive) r.graphics.destroy() }
        this.riders = []

        // Wave complete bonus
        const bonus = 10 + this.wave * 5
        this.coins += bonus
        this.updateUI()
        this.playSfx('wave-complete', 0.6)

        const msg = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, `Wave ${this.wave} Complete! +🐟${bonus}`, {
          fontSize: '28px', fontFamily: 'Arial', color: '#4CAF50', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(200)

        this.tweens.add({
          targets: msg,
          y: 280,
          alpha: 0,
          delay: 1500,
          duration: 500,
          onComplete: () => msg.destroy(),
        })

        // Re-enable start wave button
        this.startWaveBtnBg.setFillStyle(0x4CAF50)
        this.startWaveBtnBg.setInteractive({ useHandCursor: true })
        this.startWaveBtnText.setText(`▶ Start Wave ${this.wave + 1}`)
        this.saveGame()
      }
    }
  }

  spawnRider(tower: Tower) {
    // Find the nearest path point to this tower
    let nearestIdx = 0
    let nearestDist = Infinity
    for (let i = 0; i < this.activePath.length; i++) {
      const d = Phaser.Math.Distance.Between(tower.x, tower.y, this.activePath[i][0], this.activePath[i][1])
      if (d < nearestDist) { nearestDist = d; nearestIdx = i }
    }

    const [sx, sy] = this.activePath[nearestIdx]
    const container = this.add.container(sx, sy).setDepth(22)

    // Use bass-rider sprite
    const bassRiderSprite = this.add.image(0, 0, PROJECTILE_SPRITES.BASS_RIDER)
      .setScale(0.8)
    container.add(bassRiderSprite)

    // Wiggle animation
    this.tweens.add({
      targets: container,
      scaleY: { from: 0.9, to: 1.1 },
      duration: 200,
      yoyo: true,
      repeat: -1,
    })

    // Small water trail
    const trail = this.add.graphics().setDepth(21)
    container.add(trail)
    trail.fillStyle(0x90CAF9, 0.3)
    trail.fillEllipse(-14, 0, 8, 4)

    // Throw animation — sprite swap
    if (tower.bearSprite) {
      const sp = (TOWER_SPRITES as any)[tower.def.key]
      if (sp) {
        tower.bearSprite.setTexture(sp.throw || sp.bear)
        this.time.delayedCall(300, () => {
          if (tower.bearSprite) tower.bearSprite.setTexture(sp.bear)
        })
      }
    }

    this.riders.push({
      graphics: container,
      damage: tower.effectiveDamage,
      speed: tower.def.projectileSpeed + (tower.upgrades.speed * 20),
      pathIndex: nearestIdx,
      x: sx, y: sy,
      alive: true,
      towerDef: tower.def,
    })
  }

  // === SAVE / LOAD SYSTEM ===

  static readonly SAVE_KEY = 'hot-tub-defense-save'

  saveGame() {
    const state = {
      coins: this.coins,
      lives: this.lives,
      wave: this.wave,
      testingMode: this.testingMode,
      towers: this.towers.map(t => ({
        key: t.def.key,
        x: t.x,
        y: t.y,
        upgrades: { ...t.upgrades },
      })),
    }
    try {
      localStorage.setItem(GameScene.SAVE_KEY, JSON.stringify(state))
    } catch (_) { /* storage full or unavailable */ }
  }

  static hasSave(): boolean {
    try {
      return localStorage.getItem(GameScene.SAVE_KEY) !== null
    } catch { return false }
  }

  static clearSave() {
    try { localStorage.removeItem(GameScene.SAVE_KEY) } catch (_) {}
  }

  loadSavedGame() {
    try {
      const raw = localStorage.getItem(GameScene.SAVE_KEY)
      if (!raw) return false
      const state = JSON.parse(raw)

      this.testingMode = state.testingMode ?? false

      // Temporarily mute sound and give infinite coins for tower placement
      const origSfx = this.sfxEnabled
      this.sfxEnabled = false
      this.coins = 999999

      // Rebuild towers
      if (Array.isArray(state.towers)) {
        for (const t of state.towers) {
          if (!TOWERS[t.key]) continue
          this.placeTower(t.x, t.y, t.key)
          // Apply saved upgrades
          const tower = this.towers[this.towers.length - 1]
          if (t.upgrades) {
            tower.upgrades = { damage: t.upgrades.damage || 0, range: t.upgrades.range || 0, speed: t.upgrades.speed || 0 }
            // Recalculate effective stats
            tower.effectiveDamage = Math.floor(tower.def.damage * (1 + tower.upgrades.damage * 0.25))
            tower.effectiveRange = Math.floor(tower.def.range * (1 + tower.upgrades.range * 0.15))
            tower.effectiveFireRate = Math.floor(tower.def.fireRate * (1 - tower.upgrades.speed * 0.15))
            if (tower.def.splashRadius) {
              tower.effectiveSplashRadius = Math.floor(tower.def.splashRadius * (1 + tower.upgrades.damage * 0.15))
            }
            // Update level stars
            const totalLevel = tower.upgrades.damage + tower.upgrades.range + tower.upgrades.speed
            if (totalLevel > 0 && tower.levelText) {
              tower.levelText.setText('⭐'.repeat(Math.min(totalLevel, 9)))
            }
          }
        }
      }

      // Restore actual saved values
      this.sfxEnabled = origSfx
      this.coins = state.coins ?? 100
      this.lives = state.lives ?? 20
      this.wave = state.wave ?? 0

      this.updateUI()
      this.startWaveBtnText.setText(`▶ Start Wave ${this.wave + 1}`)
      return true
    } catch (_) {
      return false
    }
  }

  // === END SAVE / LOAD ===

  fireProjectile(tower: Tower, targetIdx: number) {
    const def = tower.def
    const enemy = this.enemies[targetIdx]
    this.playSfx('throw', 0.2)

    // === THROW ANIMATION (sprite swap) ===
    if (tower.bearSprite) {
      const towerType = def.key as TowerType
      const sprites = TOWER_SPRITES[towerType]
      const idleKey = sprites.bear
      const throwKey = sprites.throw || idleKey

      // Swap to throw sprite
      tower.bearSprite.setTexture(throwKey)
      // Swap back to idle after delay
      this.time.delayedCall(300, () => {
        if (tower.bearSprite) tower.bearSprite.setTexture(idleKey)
      })
    }

    // Legacy arm animation compatibility (no-op now)
    if (false) {
      const restY = tower.armRestY
      this.tweens.killTweensOf(tower.arm)
      this.tweens.add({
        targets: tower.arm,
        rotation: -1.0,
        y: restY - 8,
        duration: 100,
        ease: 'Power3',
        onComplete: () => {
          tower.fishInHand.setAlpha(0)
          this.tweens.add({
            targets: tower.arm,
            rotation: 0.6,
            y: restY + 6,
            duration: 200,
            ease: 'Sine.easeIn',
            onComplete: () => {
              tower.fishInHand.setAlpha(1)
              this.tweens.add({
                targets: tower.arm,
                rotation: 0,
                y: restY,
                duration: 200,
              ease: 'Sine.easeOut',
            })
          },
        })
      },
    })
    } // end legacy arm animation

    // 3. Create the actual projectile using sprite
    const projContainer = this.add.container(tower.x + 14, tower.y - 16).setDepth(35)
    
    // Select projectile sprite based on tower type
    let projSprite: string
    switch (def.key) {
      case 'honey': projSprite = PROJECTILE_SPRITES.HONEY; break
      case 'salmon': projSprite = PROJECTILE_SPRITES.SALMON; break
      case 'bass': projSprite = PROJECTILE_SPRITES.BASS; break
      case 'pufferfish': projSprite = PROJECTILE_SPRITES.PUFFERFISH; break
      case 'swordfish': projSprite = PROJECTILE_SPRITES.SWORDFISH; break
      case 'eel': projSprite = PROJECTILE_SPRITES.EEL; break
      case 'catfish': projSprite = PROJECTILE_SPRITES.CATFISH; break
      case 'shark': projSprite = PROJECTILE_SPRITES.SHARK; break
      case 'barracuda': {
        // Use tiered projectile sprite
        const totalUpgrades = tower.upgrades.damage + tower.upgrades.range + tower.upgrades.speed
        const tier = getTierIndex(totalUpgrades)
        const tiered = TIERED_SPRITES.barracuda
        projSprite = tiered ? tiered[tier].proj : PROJECTILE_SPRITES.BARRACUDA
        break
      }
      case 'blobfish': projSprite = PROJECTILE_SPRITES.BLOBFISH; break
      default: projSprite = PROJECTILE_SPRITES.SALMON; break
    }
    
    const projImage = this.add.image(0, 0, projSprite).setScale(0.6)
    projContainer.add(projImage)

    // Rotate projectile toward target
    const angle = Phaser.Math.Angle.Between(tower.x, tower.y, enemy.graphics.x, enemy.graphics.y)
    projContainer.setRotation(angle)

    const isGrenade = def.special === 'grenade'
    const startX = tower.x + 14
    const startY = tower.y - 16
    const totalDist = isGrenade ? Phaser.Math.Distance.Between(startX, startY, enemy.graphics.x, enemy.graphics.y) : 0

    this.projectiles.push({
      graphics: projContainer,
      targetEnemy: targetIdx,
      tx: enemy.graphics.x,
      ty: enemy.graphics.y,
      damage: tower.effectiveDamage,
      speed: def.projectileSpeed,
      special: def.special,
      splashRadius: tower.effectiveSplashRadius || def.splashRadius,
      slowAmount: def.slowAmount,
      slowDuration: def.slowDuration,
      chainCount: def.chainCount,
      pierced: new Set(),
      alive: true,
      isGrenade,
      startX: isGrenade ? startX : undefined,
      startY: isGrenade ? startY : undefined,
      totalDist: isGrenade ? totalDist : undefined,
      traveled: isGrenade ? 0 : undefined,
      arcHeight: isGrenade ? Math.max(60, totalDist * 0.4) : undefined,
      isBoomerang: def.special === 'boomerang',
      returning: false,
      originX: tower.x + 14,
      originY: tower.y - 16,
      boomerangPierced: def.special === 'boomerang' ? new Set() : undefined,
    })
  }
}
